import React, { useEffect, useState, useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Search, ArrowRight, Rss } from "lucide-react";
import { api } from "@/lib/api";
import SEO from "@/components/SEO";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import HeaderV4 from "@/components/header-v4/HeaderV4";
import FooterV4 from "@/components/footer-v4/FooterV4";

const APP_URL = process.env.REACT_APP_BACKEND_URL || "";
const V4_ENABLED = (process.env.REACT_APP_HEADER_V4_ENABLED ?? "true") !== "false";

function formatDate(iso, lang = "en") {
    if (!iso) return "";
    try {
        return new Date(iso).toLocaleDateString(lang, {
            year: "numeric", month: "short", day: "numeric",
        });
    } catch {
        return iso.slice(0, 10);
    }
}

export default function Blog() {
    const { t, i18n } = useTranslation();
    const [searchParams, setSearchParams] = useSearchParams();
    const [posts, setPosts] = useState([]);
    const [categories, setCategories] = useState([]);
    const activeCategory = searchParams.get("category") || "";
    const q = searchParams.get("q") || "";
    const [loading, setLoading] = useState(true);

    // Local input state so typing doesn't spam the URL every keystroke;
    // we commit to the URL on debounce.
    const [qInput, setQInput] = useState(q);

    // Debounced sync qInput → URL (300ms)
    useEffect(() => {
        const h = setTimeout(() => {
            const next = new URLSearchParams(searchParams);
            if (qInput) next.set("q", qInput); else next.delete("q");
            if (next.toString() !== searchParams.toString()) {
                setSearchParams(next, { replace: true });
            }
        }, 300);
        return () => clearTimeout(h);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [qInput]);

    const setCategory = (slug) => {
        const next = new URLSearchParams(searchParams);
        if (slug) next.set("category", slug); else next.delete("category");
        setSearchParams(next, { replace: true });
    };

    useEffect(() => {
        (async () => {
            setLoading(true);
            try {
                const [postsResp, catsResp] = await Promise.all([
                    api.blogPosts({ category: activeCategory || undefined, q: q || undefined, limit: 50 }),
                    api.blogCategories(),
                ]);
                setPosts(postsResp.items || []);
                setCategories(catsResp.items || []);
            } finally {
                setLoading(false);
            }
        })();
    }, [activeCategory, q]);

    const itemListJsonLd = useMemo(
        () => ({
            "@context": "https://schema.org",
            "@type": "ItemList",
            itemListElement: (posts || []).map((p, idx) => ({
                "@type": "ListItem",
                position: idx + 1,
                url: `${APP_URL}/blog/${p.slug}`,
                name: p.title,
            })),
        }),
        [posts],
    );

    return (
        <div className="min-h-screen bg-[var(--color-bg)] text-text-main">
            <SEO
                title={t("blog.title")}
                description={t("blog.subtitle")}
                canonical="/blog"
                jsonLd={itemListJsonLd}
            />
            {/* Header — F12.1 chrome swap. Legacy inline nav kept behind
                REACT_APP_HEADER_V4_ENABLED=false. */}
            {V4_ENABLED ? (
                <HeaderV4 />
            ) : (
                <header className="sticky top-0 z-30 border-b border-[var(--color-border)] bg-[var(--color-bg)]/85 backdrop-blur-xl">
                    <div className="mx-auto max-w-6xl flex items-center justify-between px-6 py-4">
                        <Link
                            to="/"
                            data-testid="blog-header-logo"
                            className="flex items-center gap-2 group"
                        >
                            <span className="h-8 w-8 rounded-lg gradient-bg-cta grid place-items-center shadow-md">
                                <span className="text-[13px] font-bold text-white">G</span>
                            </span>
                            <span className="text-sm font-semibold tracking-tight">
                                Global Lead Suite
                            </span>
                        </Link>
                        <div className="flex items-center gap-3">
                            <Link
                                to="/app/subscription"
                                className="text-sm text-text-secondary hover:text-text-main transition-colors"
                            >
                                {t("nav.pricing")}
                            </Link>
                            <LanguageSwitcher />
                            <Link
                                to="/dashboard"
                                data-testid="blog-header-cta"
                                className="rounded-full gradient-bg-cta px-4 py-1.5 text-sm font-medium text-white glow-primary"
                            >
                                {t("cta.get_started")}
                            </Link>
                        </div>
                    </div>
                </header>
            )}

            <main className="mx-auto max-w-6xl px-6 py-16">
                <motion.section
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                    className="mb-12"
                >
                    <div className="inline-flex items-center gap-2 rounded-full border border-[var(--color-border)] bg-[var(--color-card)] px-3 py-1.5 text-xs uppercase tracking-widest text-text-secondary mb-6">
                        <Rss className="h-3 w-3 text-[var(--color-secondary)]" />
                        Blog
                    </div>
                    <h1 className="text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight leading-tight">
                        <span className="gradient-text">{t("blog.title")}</span>
                    </h1>
                    <p className="mt-4 text-lg text-text-secondary max-w-2xl">
                        {t("blog.subtitle")}
                    </p>
                </motion.section>

                {/* Filters */}
                <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-10">
                    <div className="relative flex-1">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-text-secondary" />
                        <input
                            data-testid="blog-search"
                            value={qInput}
                            onChange={(e) => setQInput(e.target.value)}
                            placeholder={t("blog.search_placeholder")}
                            className="w-full rounded-xl border border-[var(--color-border)] bg-[var(--color-card)] pl-10 pr-4 py-2.5 text-sm outline-none focus:border-[var(--color-primary)]/60 transition-colors"
                        />
                    </div>
                    <div className="flex flex-wrap gap-2">
                        <button
                            data-testid="blog-cat-all"
                            onClick={() => setCategory("")}
                            className={`rounded-full border px-3 py-1.5 text-xs uppercase tracking-widest transition-colors ${
                                activeCategory === ""
                                    ? "border-[var(--color-primary)] bg-[var(--color-primary)]/15 text-[var(--color-primary)]"
                                    : "border-[var(--color-border)] text-text-secondary hover:text-text-main"
                            }`}
                        >
                            {t("blog.all_categories")}
                        </button>
                        {categories.map((c) => (
                            <button
                                key={c.slug}
                                data-testid={`blog-cat-${c.slug}`}
                                onClick={() => setCategory(c.slug)}
                                className={`rounded-full border px-3 py-1.5 text-xs uppercase tracking-widest transition-colors ${
                                    activeCategory === c.slug
                                        ? "border-[var(--color-primary)] bg-[var(--color-primary)]/15 text-[var(--color-primary)]"
                                        : "border-[var(--color-border)] text-text-secondary hover:text-text-main"
                                }`}
                            >
                                {c.name}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Posts */}
                {loading ? (
                    <div data-testid="blog-loading" className="text-text-secondary">
                        …
                    </div>
                ) : posts.length === 0 ? (
                    <div
                        data-testid="blog-empty"
                        className="rounded-2xl border border-dashed border-[var(--color-border)] p-16 text-center text-text-secondary"
                    >
                        {t("blog.empty")}
                    </div>
                ) : (
                    <div data-testid="blog-grid" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {posts.map((p, idx) => (
                            <motion.article
                                key={p.id}
                                initial={{ opacity: 0, y: 12 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.4, delay: idx * 0.05 }}
                                className="group rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] overflow-hidden hover:border-[var(--color-primary)]/50 transition-colors flex flex-col"
                                data-testid={`blog-card-${p.slug}`}
                            >
                                <div className="p-6 flex-1 flex flex-col">
                                    <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-text-secondary mb-3">
                                        <span className="rounded-full border border-[var(--color-border)] px-2 py-0.5">
                                            {p.category_slug}
                                        </span>
                                        <span>{formatDate(p.published_at, i18n.language)}</span>
                                    </div>
                                    <Link
                                        to={`/blog/${p.slug}`}
                                        className="text-lg font-semibold leading-snug hover:text-[var(--color-primary)] transition-colors line-clamp-2"
                                    >
                                        {p.title}
                                    </Link>
                                    {p.excerpt && (
                                        <p className="mt-3 text-sm text-text-secondary line-clamp-3">
                                            {p.excerpt}
                                        </p>
                                    )}
                                    <div className="mt-auto pt-5">
                                        <Link
                                            to={`/blog/${p.slug}`}
                                            data-testid={`blog-read-${p.slug}`}
                                            className="inline-flex items-center gap-1 text-sm text-[var(--color-secondary)] group-hover:gap-2 transition-all"
                                        >
                                            {t("cta.read_more")}
                                            <ArrowRight className="h-3.5 w-3.5" />
                                        </Link>
                                    </div>
                                </div>
                            </motion.article>
                        ))}
                    </div>
                )}
            </main>

            {V4_ENABLED ? (
                <FooterV4 />
            ) : (
                <footer className="border-t border-[var(--color-border)] mt-24 py-8 text-center text-xs text-text-secondary">
                    {t("_static_legal.copyright", { year: new Date().getFullYear() })}
                </footer>
            )}
        </div>
    );
}
